import { IntlShape, MessageDescriptor, defineMessages } from 'react-intl';
import { Music24QuotaKind } from 'fym-common/src/license/music24Plans';
import { Music24PlanCapabilities, SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { MUSIC24_FEATURE_ROWS } from '../../config/music24PlanFeatureRows';

/**
 * Every string the upgrade modal can say. IDs are static literals so `pnpm trans` can extract
 * them; only the lookup by feature is dynamic.
 */
export const upgradeMessages = defineMessages({
  close: {
    id: 'app.sml.upgrade.close',
    defaultMessage: 'Close',
  },
  notNow: {
    id: 'app.sml.upgrade.action.notNow',
    defaultMessage: 'Not now',
  },
  upgradeCtaBusy: {
    id: 'app.sml.upgrade.action.upgradeBusy',
    defaultMessage: 'Opening checkout…',
  },
  // Ruling 8: a team member never sees a checkout button — only the workspace owner manages
  // billing. This replaces the primary CTA wherever `useMyTeamRole()` reads `MEMBER`.
  memberCta: {
    id: 'app.sml.upgrade.action.memberCta',
    defaultMessage: 'Ask the workspace owner to upgrade.',
  },
  bannerTitleDays: {
    id: 'app.sml.upgrade.banner.titleDays',
    defaultMessage: '{count, plural, =1 {# more day} other {# more days}} of this chart',
  },
  bannerTitleResults: {
    id: 'app.sml.upgrade.banner.titleResults',
    defaultMessage: '{count, number} more results in this table',
  },
  bannerTitleGeneric: {
    id: 'app.sml.upgrade.banner.titleGeneric',
    defaultMessage: 'More of this on {plan}',
  },
  bannerNote: {
    id: 'app.sml.upgrade.banner.note',
    defaultMessage: '{description} From {price}/month.',
  },
  bannerCta: {
    id: 'app.sml.upgrade.banner.cta',
    defaultMessage: 'See {plan}',
  },
  successTitle: {
    id: 'app.sml.upgrade.success.title',
    defaultMessage: '{plan} is on',
  },
  successBody: {
    id: 'app.sml.upgrade.success.body',
    defaultMessage: 'It is loading behind this window.',
  },
  successRenews: {
    id: 'app.sml.upgrade.success.renews',
    defaultMessage: '{price}/month · renews {date}',
  },
  successBack: {
    id: 'app.sml.upgrade.success.back',
    defaultMessage: 'Back to what you clicked',
  },
  successReceipt: {
    id: 'app.sml.upgrade.success.receipt',
    defaultMessage: 'Receipt sent to {email}',
  },
  // Design b2 — the monthly-quota layout (`M24_LIMIT_QUOTA`).
  quotaKindScheduledPost: {
    id: 'app.sml.upgrade.quota.kind.scheduledPost',
    defaultMessage: 'auto-posts',
  },
  quotaKindVideoCredit: {
    id: 'app.sml.upgrade.quota.kind.videoCredit',
    defaultMessage: 'video credits',
  },
  quotaKindAiInsight: {
    id: 'app.sml.upgrade.quota.kind.aiInsight',
    defaultMessage: 'AI insights',
  },
  quotaKindCoverAiPicture: {
    id: 'app.sml.upgrade.quota.kind.coverAiPicture',
    defaultMessage: 'AI cover pictures',
  },
  quotaKindOutreachReveal: {
    id: 'app.sml.upgrade.quota.kind.outreachReveal',
    defaultMessage: 'pitches',
  },
  quotaKindOutreachSend: {
    id: 'app.sml.upgrade.quota.kind.outreachSend',
    defaultMessage: 'pitch emails',
  },
  quotaUsage: {
    id: 'app.sml.upgrade.quota.usage',
    defaultMessage: 'You used {used, number} of {limit, number} {kind} this month.',
  },
  quotaFraction: {
    id: 'app.sml.upgrade.quota.fraction',
    defaultMessage: '{used, number} / {limit, number}',
  },
  quotaWaitFor: {
    id: 'app.sml.upgrade.quota.waitFor',
    defaultMessage: 'Wait for {date}',
  },
  quotaKindInMonth: {
    id: 'app.sml.upgrade.quota.kindInMonth',
    defaultMessage: '{kind} in {month}',
  },
  // Design b: the plan list and its actions.
  upgradeTo: {
    id: 'app.sml.upgrade.action.upgradeTo',
    defaultMessage: 'Upgrade to {plan}',
  },
  compareAllPlans: {
    id: 'app.sml.upgrade.action.compareAllPlans',
    defaultMessage: 'Compare all plans',
  },
  plansLabel: {
    id: 'app.sml.upgrade.plans.label',
    defaultMessage: 'Plans that include this',
  },
  planPrice: {
    id: 'app.sml.upgrade.plans.price',
    defaultMessage: '{price} / month',
  },
  planFact: {
    id: 'app.sml.upgrade.plans.fact',
    defaultMessage: '{label}: {value}',
  },
  quotaResetsOn: {
    id: 'app.sml.upgrade.quota.resetsOn',
    defaultMessage: 'Resets {date}',
  },
  quotaCurrentPlan: {
    id: 'app.sml.upgrade.quota.currentPlan',
    defaultMessage: '{plan} plan',
  },
});

/** One message per `Music24QuotaKind`, shared by the offer title and the quota meter row. */
export const quotaKindMessages: Record<Music24QuotaKind, MessageDescriptor> = {
  scheduledPost: upgradeMessages.quotaKindScheduledPost,
  videoCredit: upgradeMessages.quotaKindVideoCredit,
  aiInsight: upgradeMessages.quotaKindAiInsight,
  coverAiPicture: upgradeMessages.quotaKindCoverAiPicture,
  outreachReveal: upgradeMessages.quotaKindOutreachReveal,
  outreachSend: upgradeMessages.quotaKindOutreachSend,
};

interface FeatureCopy {
  /** Mono label in the header strip. */
  label: MessageDescriptor;
  /** H2 — names the click. */
  title: MessageDescriptor;
  /** H2 for a caller that can name the artist, track or playlist. */
  titleWithSubject: MessageDescriptor;
  /** One-line lede under the H2. */
  lede: MessageDescriptor;
  /** The lede with no web price, for a native app's web view (App Store 3.1.1). Only a lede that
   *  quotes `{price}` needs one; the others read the same there. */
  ledeWithoutPrice?: MessageDescriptor;
  /** Value-list row. */
  valueTitle: MessageDescriptor;
  valueDescription: MessageDescriptor;
}

/**
 * Flat on purpose: `pnpm trans` extracts from the defineMessages call, so every descriptor has to
 * live in one. `featureCopy` below only groups the references.
 */
export const featureMessages = defineMessages({
  historyLabel: { id: 'app.sml.upgrade.feature.history.label', defaultMessage: 'Extended history' },
  historyTitle: {
    id: 'app.sml.upgrade.feature.history.title',
    defaultMessage: 'See all {days, plural, =1 {# day} other {# days}} of playlist history',
  },
  historyTitleWithSubject: {
    id: 'app.sml.upgrade.feature.history.titleWithSubject',
    defaultMessage: "See all {days, plural, =1 {# day} other {# days}} of {subject}'s playlist history",
  },
  historyLede: {
    id: 'app.sml.upgrade.feature.history.lede',
    defaultMessage:
      'Your plan keeps {current, plural, =0 {no history} =1 {# day} other {# days}}. {extra, plural, =1 {The other # day is} other {The other # days are}} already collected — {plan} just shows them.',
  },
  historyValueTitle: { id: 'app.sml.upgrade.feature.history.valueTitle', defaultMessage: 'Longer history' },
  historyValueDescription: {
    id: 'app.sml.upgrade.feature.history.valueDescription',
    defaultMessage: 'Playlist and track history back {days, plural, =1 {# day} other {# days}}.',
  },

  browseDepthLabel: { id: 'app.sml.upgrade.feature.browseDepth.label', defaultMessage: 'Deeper results' },
  browseDepthTitle: {
    id: 'app.sml.upgrade.feature.browseDepth.title',
    defaultMessage: 'Browse the next {count, number} results',
  },
  browseDepthTitleWithSubject: {
    id: 'app.sml.upgrade.feature.browseDepth.titleWithSubject',
    defaultMessage: 'Browse the next {count, number} results in {subject}',
  },
  browseDepthLede: {
    id: 'app.sml.upgrade.feature.browseDepth.lede',
    defaultMessage:
      'Your plan stops at {current, number} results. {plan} reads to {target, number} — the rows are ranked already.',
  },
  browseDepthValueTitle: { id: 'app.sml.upgrade.feature.browseDepth.valueTitle', defaultMessage: 'Deeper results' },
  browseDepthValueDescription: {
    id: 'app.sml.upgrade.feature.browseDepth.valueDescription',
    defaultMessage: 'Page through {count, number} results in every table.',
  },

  geographyLabel: { id: 'app.sml.upgrade.feature.geography.label', defaultMessage: 'Audience by country' },
  geographyTitle: { id: 'app.sml.upgrade.feature.geography.title', defaultMessage: 'See where the listeners are' },
  geographyTitleWithSubject: {
    id: 'app.sml.upgrade.feature.geography.titleWithSubject',
    defaultMessage: 'See where {subject} is being played',
  },
  geographyLede: {
    id: 'app.sml.upgrade.feature.geography.lede',
    defaultMessage: 'Your plan shows the global figure. {plan} splits every chart by country.',
  },
  geographyValueTitle: { id: 'app.sml.upgrade.feature.geography.valueTitle', defaultMessage: 'Audience by country' },
  geographyValueDescription: {
    id: 'app.sml.upgrade.feature.geography.valueDescription',
    defaultMessage: 'Filter any chart to one market, or compare them.',
  },

  serviceLabel: { id: 'app.sml.upgrade.feature.service.label', defaultMessage: 'Music service filters' },
  serviceTitle: { id: 'app.sml.upgrade.feature.service.title', defaultMessage: 'Split the numbers by music service' },
  serviceTitleWithSubject: {
    id: 'app.sml.upgrade.feature.service.titleWithSubject',
    defaultMessage: 'Split {subject} by music service',
  },
  serviceLede: {
    id: 'app.sml.upgrade.feature.service.lede',
    defaultMessage: '{plan} reads Spotify, Apple Music and the rest apart instead of as one total.',
  },
  serviceValueTitle: { id: 'app.sml.upgrade.feature.service.valueTitle', defaultMessage: 'Music service filters' },
  serviceValueDescription: {
    id: 'app.sml.upgrade.feature.service.valueDescription',
    defaultMessage: 'Read each streaming service on its own.',
  },

  insightsLabel: { id: 'app.sml.upgrade.feature.insights.label', defaultMessage: 'Insights & trends' },
  insightsTitle: { id: 'app.sml.upgrade.feature.insights.title', defaultMessage: 'See what moved, and why' },
  insightsTitleWithSubject: {
    id: 'app.sml.upgrade.feature.insights.titleWithSubject',
    defaultMessage: 'See what moved for {subject}, and why',
  },
  insightsLede: {
    id: 'app.sml.upgrade.feature.insights.lede',
    defaultMessage: '{plan} adds the trend and listening insights on top of the raw counts.',
  },
  insightsValueTitle: { id: 'app.sml.upgrade.feature.insights.valueTitle', defaultMessage: 'Insights & trends' },
  insightsValueDescription: {
    id: 'app.sml.upgrade.feature.insights.valueDescription',
    defaultMessage: 'Listening insights and trend detection on your catalogue.',
  },

  exportLabel: { id: 'app.sml.upgrade.feature.export.label', defaultMessage: 'Data export' },
  exportTitle: { id: 'app.sml.upgrade.feature.export.title', defaultMessage: 'Take this table with you' },
  exportTitleWithSubject: {
    id: 'app.sml.upgrade.feature.export.titleWithSubject',
    defaultMessage: 'Take {subject} with you as a CSV',
  },
  exportLede: {
    id: 'app.sml.upgrade.feature.export.lede',
    defaultMessage: '{plan} exports any table to CSV, ready for a spreadsheet or a pitch deck.',
  },
  exportValueTitle: { id: 'app.sml.upgrade.feature.export.valueTitle', defaultMessage: 'Export to CSV' },
  exportValueDescription: {
    id: 'app.sml.upgrade.feature.export.valueDescription',
    defaultMessage: 'Download any chart or table as a CSV.',
  },

  // The native apps still read the six SmartLinks ids (scripts/music24-native/i18n-keys.txt). The
  // web has no SmartLinks prompt: SmartLinks come with every plan. Keep them until the apps stop.
  smartLinksLabel: { id: 'app.sml.upgrade.feature.smartLinks.label', defaultMessage: 'Smartlinks' },
  smartLinksTitle: {
    id: 'app.sml.upgrade.feature.smartLinks.title',
    defaultMessage: 'Open your Smartlinks and pre-save pages',
  },
  smartLinksTitleWithSubject: {
    id: 'app.sml.upgrade.feature.smartLinks.titleWithSubject',
    defaultMessage: 'Open Smartlinks and pre-save pages for {subject}',
  },
  smartLinksLede: {
    id: 'app.sml.upgrade.feature.smartLinks.lede',
    defaultMessage: 'Smartlinks come with every plan. One release page for every service, and the clicks behind it.',
  },
  smartLinksValueTitle: {
    id: 'app.sml.upgrade.feature.smartLinks.valueTitle',
    defaultMessage: 'Smartlinks and pre-save pages',
  },
  smartLinksValueDescription: {
    id: 'app.sml.upgrade.feature.smartLinks.valueDescription',
    defaultMessage: 'Unlimited release pages, pre-saves and click analytics.',
  },

  followLabel: { id: 'app.sml.upgrade.feature.follow.label', defaultMessage: 'Artists tracked' },
  followTitle: { id: 'app.sml.upgrade.feature.follow.title', defaultMessage: 'Follow up to {artists, number} artists' },
  followTitleWithSubject: {
    id: 'app.sml.upgrade.feature.follow.titleWithSubject',
    defaultMessage: 'Follow {subject}, and up to {artists, number} artists',
  },
  followLede: {
    id: 'app.sml.upgrade.feature.follow.lede',
    defaultMessage: 'Your plan follows {current, number}. {plan} follows {target, number}, tracks included.',
  },
  followValueTitle: { id: 'app.sml.upgrade.feature.follow.valueTitle', defaultMessage: 'More artists tracked' },
  followValueDescription: {
    id: 'app.sml.upgrade.feature.follow.valueDescription',
    defaultMessage: 'Follow {artists, number} artists and {tracks, number} tracks.',
  },

  apiAccessLabel: { id: 'app.sml.upgrade.feature.apiAccess.label', defaultMessage: 'API access' },
  apiAccessTitle: {
    id: 'app.sml.upgrade.feature.apiAccess.title',
    defaultMessage: 'Call the MUSIC24 API directly',
  },
  apiAccessTitleWithSubject: {
    id: 'app.sml.upgrade.feature.apiAccess.titleWithSubject',
    defaultMessage: 'Call the MUSIC24 API for {subject}',
  },
  apiAccessLede: {
    id: 'app.sml.upgrade.feature.apiAccess.lede',
    defaultMessage:
      '{plan} adds a REST API with your own tokens, so you can build dashboards and automations on this data.',
  },
  apiAccessValueTitle: { id: 'app.sml.upgrade.feature.apiAccess.valueTitle', defaultMessage: 'API access' },
  apiAccessValueDescription: {
    id: 'app.sml.upgrade.feature.apiAccess.valueDescription',
    defaultMessage: 'Mint your own tokens and call the MUSIC24 API directly.',
  },

  // Release, Promote, Measure — the 13 features the free-plans limits gate. Every `title`/`lede`
  // reuses the numbers `MUSIC24_FEATURE_ROWS` already formats (via `featureRowValue` below), so a
  // limit only ever lives in `music24PlanCapabilities`.
  pagesTitle: { id: 'app.sml.upgrade.feature.pages.title', defaultMessage: 'Publish more pages' },
  pagesBody: {
    id: 'app.sml.upgrade.feature.pages.body',
    defaultMessage: "Your published-page limit is {currentText}. {plan}'s is {targetText}.",
  },
  pagesValueDescription: {
    id: 'app.sml.upgrade.feature.pages.valueDescription',
    defaultMessage: '{targetText} published SmartLink and pre-save pages.',
  },

  artistsTitle: { id: 'app.sml.upgrade.feature.artists.title', defaultMessage: 'Add another artist' },
  artistsBody: {
    id: 'app.sml.upgrade.feature.artists.body',
    // The native apps render this id with the same three values (`UpgradeCopy.kt`, `UpgradeTeamNote.kt`).
    defaultMessage:
      'Your artist limit is {currentText}. On {plan}: {targetText}. Each extra artist costs 20 % less than the plan.',
  },
  artistsValueDescription: {
    id: 'app.sml.upgrade.feature.artists.valueDescription',
    defaultMessage: 'Artists: {targetText}. Each extra artist costs 20 % less than the plan.',
  },

  seatsTitle: { id: 'app.sml.upgrade.feature.seats.title', defaultMessage: 'Invite your team' },
  seatsBody: {
    id: 'app.sml.upgrade.feature.seats.body',
    defaultMessage: "Your team-seat limit is {currentText}. {plan}'s is {targetText}.",
  },
  seatsValueDescription: {
    id: 'app.sml.upgrade.feature.seats.valueDescription',
    defaultMessage: '{targetText} team seats to invite.',
  },

  socialAccountsTitle: {
    id: 'app.sml.upgrade.feature.socialAccounts.title',
    defaultMessage: 'Connect more social accounts',
  },
  socialAccountsBody: {
    id: 'app.sml.upgrade.feature.socialAccounts.body',
    defaultMessage: "Your connected-account limit is {currentText}. {plan}'s is {targetText}.",
  },
  socialAccountsValueDescription: {
    id: 'app.sml.upgrade.feature.socialAccounts.valueDescription',
    defaultMessage: '{targetText} connected social accounts for auto-posting.',
  },

  scheduledPostsTitle: {
    id: 'app.sml.upgrade.feature.scheduledPosts.title',
    defaultMessage: 'Schedule more posts this month',
  },
  scheduledPostsBody: {
    id: 'app.sml.upgrade.feature.scheduledPosts.body',
    defaultMessage: "Your scheduled-post allowance is {currentText}. {plan}'s is {targetText}.",
  },
  scheduledPostsValueDescription: {
    id: 'app.sml.upgrade.feature.scheduledPosts.valueDescription',
    defaultMessage: '{targetText} of scheduled auto-posts.',
  },

  videoCreditsTitle: {
    id: 'app.sml.upgrade.feature.videoCredits.title',
    defaultMessage: 'Get more video credits this month',
  },
  videoCreditsBody: {
    id: 'app.sml.upgrade.feature.videoCredits.body',
    defaultMessage: "Your video credits are {currentText}. {plan}'s are {targetText}.",
  },
  videoCreditsValueDescription: {
    id: 'app.sml.upgrade.feature.videoCredits.valueDescription',
    defaultMessage: '{targetText} of video credits.',
  },

  aiPriorityLabel: { id: 'app.sml.upgrade.feature.aiPriority.label', defaultMessage: 'Priority AI' },
  aiPriorityTitle: { id: 'app.sml.upgrade.feature.aiPriority.title', defaultMessage: 'AI is busy this month' },
  aiPriorityLede: {
    id: 'app.sml.upgrade.feature.aiPriority.lede',
    defaultMessage:
      'The AI for Free and trial accounts is full until next month. Upgrade to {plan} for priority access, from {price} a month.',
  },
  aiPriorityLedeWithoutPrice: {
    id: 'app.sml.upgrade.feature.aiPriority.ledeWithoutPrice',
    defaultMessage:
      'The AI for Free and trial accounts is full until next month. Upgrade to {plan} for priority access.',
  },
  aiPriorityValueDescription: {
    id: 'app.sml.upgrade.feature.aiPriority.valueDescription',
    defaultMessage: 'Paid plans keep their AI features all month.',
  },

  aiInsightsTitle: { id: 'app.sml.upgrade.feature.aiInsights.title', defaultMessage: 'Get more AI insights' },
  aiInsightsBody: {
    id: 'app.sml.upgrade.feature.aiInsights.body',
    defaultMessage: "Your AI insight allowance is {currentText}. {plan}'s is {targetText}.",
  },
  aiInsightsValueDescription: {
    id: 'app.sml.upgrade.feature.aiInsights.valueDescription',
    defaultMessage: '{targetText} of AI insights.',
  },

  customDomainTitle: { id: 'app.sml.upgrade.feature.customDomain.title', defaultMessage: 'Use your own domain' },
  customDomainBody: {
    id: 'app.sml.upgrade.feature.customDomain.body',
    defaultMessage: 'Your plan uses a music24.link address. {plan} lets you connect your own domain.',
  },
  customDomainValueDescription: {
    id: 'app.sml.upgrade.feature.customDomain.valueDescription',
    defaultMessage: 'Point your own domain at your pages.',
  },

  fanExportTitle: { id: 'app.sml.upgrade.feature.fanExport.title', defaultMessage: 'Export your pre-save fans' },
  fanExportBody: {
    id: 'app.sml.upgrade.feature.fanExport.body',
    defaultMessage: 'Your plan keeps pre-save fans in MUSIC24. {plan} lets you export them as a CSV.',
  },
  fanExportValueDescription: {
    id: 'app.sml.upgrade.feature.fanExport.valueDescription',
    defaultMessage: 'Export pre-save fans as a CSV.',
  },

  // Design presave-subscriptions d3: the result first, then the plan. The trial has pre-saves.
  presaveTitle: {
    id: 'app.sml.upgrade.feature.presave.title',
    defaultMessage: 'Run pre-saves, and keep every fan.',
  },
  presaveTitleWithSubject: {
    id: 'app.sml.upgrade.feature.presave.titleWithSubject',
    defaultMessage: "Put {subject} in your fans' libraries",
  },
  presaveBody: {
    id: 'app.sml.upgrade.feature.presave.body',
    defaultMessage:
      'Fans pre-save once, and every release after that lands in their library on release day. Pre-saves and Get every release come with {plan} and up.',
  },
  presaveValueDescription: {
    id: 'app.sml.upgrade.feature.presave.valueDescription',
    defaultMessage: 'Pre-saves on every service, fans who get every release, Send to subscribers.',
  },

  realtimeAlertsTitle: {
    id: 'app.sml.upgrade.feature.realtimeAlerts.title',
    defaultMessage: 'Get alerts as they happen',
  },
  realtimeAlertsBody: {
    id: 'app.sml.upgrade.feature.realtimeAlerts.body',
    defaultMessage:
      'Your plan sends a weekly digest. {plan} sends an alert the moment a playlist adds you or a chart moves.',
  },
  realtimeAlertsValueDescription: {
    id: 'app.sml.upgrade.feature.realtimeAlerts.valueDescription',
    defaultMessage: 'Real-time playlist-add and mover alerts.',
  },

  artistPortalHistoryTitle: {
    id: 'app.sml.upgrade.feature.artistPortalHistory.title',
    defaultMessage: 'See your full Spotify for Artists history',
  },
  artistPortalHistoryBody: {
    id: 'app.sml.upgrade.feature.artistPortalHistory.body',
    defaultMessage:
      "Your Spotify for Artists and Deezer history goes back {currentText}. {plan}'s goes back {targetText}.",
  },
  artistPortalHistoryValueDescription: {
    id: 'app.sml.upgrade.feature.artistPortalHistory.valueDescription',
    defaultMessage: '{targetText} of Spotify for Artists and Deezer data.',
  },

  earningsLabel: { id: 'app.sml.upgrade.feature.earnings.label', defaultMessage: 'Earnings' },
  earningsTitle: { id: 'app.sml.upgrade.feature.earnings.title', defaultMessage: 'See what each store pays you' },
  earningsValueDescription: {
    id: 'app.sml.upgrade.feature.earnings.valueDescription',
    defaultMessage: 'Net and gross earnings by month, store and track.',
  },

  streamingSplitLabel: { id: 'app.sml.upgrade.feature.streamingSplit.label', defaultMessage: 'Stores and countries' },
  streamingSplitTitle: {
    id: 'app.sml.upgrade.feature.streamingSplit.title',
    defaultMessage: 'See where your streams come from',
  },
  streamingSplitValueDescription: {
    id: 'app.sml.upgrade.feature.streamingSplit.valueDescription',
    defaultMessage: 'Streams by store and by country.',
  },

  // Result-first prompts (pricing spec 2026-10-08): the result first, then the plan and the price.
  resultLede: { id: 'app.sml.upgrade.result.lede', defaultMessage: 'On {plan}, {price} a month.' },
  resultLedeWithoutPrice: { id: 'app.sml.upgrade.result.ledeWithoutPrice', defaultMessage: 'On {plan}.' },

  releasesTitle: { id: 'app.sml.upgrade.feature.releases.title', defaultMessage: 'Promote your next song too' },
  releasesTitleWithSubject: {
    id: 'app.sml.upgrade.feature.releases.titleWithSubject',
    defaultMessage: 'Promote {subject} too',
  },
  releasesValueDescription: {
    id: 'app.sml.upgrade.feature.releases.valueDescription',
    defaultMessage: '{targetText} with a kit, reels and a rollout.',
  },

  oldSongsTitle: { id: 'app.sml.upgrade.feature.oldSongs.title', defaultMessage: 'Push an old song again' },
  oldSongsTitleWithSubject: {
    id: 'app.sml.upgrade.feature.oldSongs.titleWithSubject',
    defaultMessage: 'Push {subject} again',
  },
  oldSongsValueDescription: {
    id: 'app.sml.upgrade.feature.oldSongs.valueDescription',
    defaultMessage: 'Spike and evergreen rollouts for the songs you already released.',
  },

  fullVideoTitle: { id: 'app.sml.upgrade.feature.fullVideo.title', defaultMessage: 'Make a video of the whole song' },
  fullVideoTitleWithSubject: {
    id: 'app.sml.upgrade.feature.fullVideo.titleWithSubject',
    defaultMessage: 'Make the lyric video for {subject}',
  },
  fullVideoValueDescription: {
    id: 'app.sml.upgrade.feature.fullVideo.valueDescription',
    defaultMessage: 'Lyric videos, visualizers and music videos of the whole song.',
  },
  aiFootageTitle: {
    id: 'app.sml.upgrade.feature.aiFootage.title',
    defaultMessage: 'Make AI clips for your music video',
  },
  aiFootageTitleWithSubject: {
    id: 'app.sml.upgrade.feature.aiFootage.titleWithSubject',
    defaultMessage: 'Make AI clips for the music video of {subject}',
  },
  aiFootageValueDescription: {
    id: 'app.sml.upgrade.feature.aiFootage.valueDescription',
    defaultMessage: 'New clips made for your song, ready to drop into your music video.',
  },

  motionArtworkTitle: { id: 'app.sml.upgrade.feature.motionArtwork.title', defaultMessage: 'Make your cover move' },
  motionArtworkTitleWithSubject: {
    id: 'app.sml.upgrade.feature.motionArtwork.titleWithSubject',
    defaultMessage: 'Make the cover of {subject} move',
  },
  motionArtworkValueDescription: {
    id: 'app.sml.upgrade.feature.motionArtwork.valueDescription',
    defaultMessage: 'Motion artwork for Apple Music, square and tall.',
  },

  kitLookTitle: { id: 'app.sml.upgrade.feature.kitLook.title', defaultMessage: 'Keep your look on every asset' },
  kitLookTitleWithSubject: {
    id: 'app.sml.upgrade.feature.kitLook.titleWithSubject',
    defaultMessage: 'Keep your look on every asset of {subject}',
  },
  kitLookValueDescription: {
    id: 'app.sml.upgrade.feature.kitLook.valueDescription',
    defaultMessage: 'Your colours, logo, font and instructions on every asset.',
  },

  kitZipTitle: { id: 'app.sml.upgrade.feature.kitZip.title', defaultMessage: 'Download the whole kit at once' },
  kitZipTitleWithSubject: {
    id: 'app.sml.upgrade.feature.kitZip.titleWithSubject',
    defaultMessage: 'Download the whole kit of {subject} at once',
  },
  kitZipValueDescription: {
    id: 'app.sml.upgrade.feature.kitZip.valueDescription',
    defaultMessage: 'Every asset of a release in one zip file.',
  },

  // design: outreach a4 — the result first, then the plan.
  outreachTitle: { id: 'app.sml.upgrade.feature.outreach.title', defaultMessage: 'Pitch your release to curators' },
  outreachTitleWithSubject: {
    id: 'app.sml.upgrade.feature.outreach.titleWithSubject',
    defaultMessage: 'Pitch {subject} to curators',
  },
  outreachValueDescription: {
    id: 'app.sml.upgrade.feature.outreach.valueDescription',
    defaultMessage:
      'Plug pitches your song to new curators every day: 1 a day on Plus, 5 on Pro, 15 for each artist on Ultra. Each pitch is a personal email from your own mailbox, with the follow-up.',
  },
  outreachSendsTitle: { id: 'app.sml.upgrade.feature.outreachSends.title', defaultMessage: 'Send more pitches' },
  outreachSendsValueDescription: {
    id: 'app.sml.upgrade.feature.outreachSends.valueDescription',
    defaultMessage: 'Pitches and follow-ups, sent at the morning of each person.',
  },
  outreachMailboxesTitle: {
    id: 'app.sml.upgrade.feature.outreachMailboxes.title',
    defaultMessage: 'Send from more mailboxes',
  },
  outreachMailboxesValueDescription: {
    id: 'app.sml.upgrade.feature.outreachMailboxes.valueDescription',
    defaultMessage:
      'Connect more of your own mailboxes to send more pitches a day, at a safe pace. Replies land in your own inbox.',
  },
  outreachAutopilotTitle: {
    id: 'app.sml.upgrade.feature.outreachAutopilot.title',
    defaultMessage: 'Let Plug send on autopilot',
  },
  outreachAutopilotValueDescription: {
    id: 'app.sml.upgrade.feature.outreachAutopilot.afterStartDescription',
    defaultMessage:
      'On Ultra, Plug checks each pitch of the day and sends the ones that pass. You do not read each one first.',
  },
  outreachRateTitle: {
    id: 'app.sml.upgrade.feature.outreachRate.title',
    defaultMessage: 'Pitch more people every day',
  },
  outreachRateValueDescription: {
    id: 'app.sml.upgrade.feature.outreachRate.valueDescription',
    defaultMessage:
      "On Ultra, Plug pitches 15 new people a day for each of your artists, and autopilot sends the pitches that pass its check. The new rate starts with tomorrow's pitches.",
  },
  outreachExportTitle: { id: 'app.sml.upgrade.feature.outreachExport.title', defaultMessage: 'Export your contacts' },
  outreachExportValueDescription: {
    id: 'app.sml.upgrade.feature.outreachExport.valueDescription',
    defaultMessage: 'A CSV of the people Plug pitched and of your imports, with their sources.',
  },

  // X connector (design x-connector a2). `{posts}` is `xPostsPerMonth` of the offered plan.
  xTitle: { id: 'app.sml.upgrade.x.title', defaultMessage: 'Post to X for you' },
  xBody: {
    id: 'app.sml.upgrade.x.body',
    defaultMessage:
      'Your plan does not post to X for you. Pro connects your X account and posts at the times you set, up to {posts} posts a month for each X account.',
  },

  renderLaneTitle: { id: 'app.sml.upgrade.feature.renderLane.title', defaultMessage: 'Get your videos sooner' },
  renderLaneValueDescription: {
    id: 'app.sml.upgrade.feature.renderLane.valueDescription',
    defaultMessage: 'Render queue: {targetText}.',
  },

  // design: trial-upsell b4. `WatermarkOffer` draws the offer; the success view reads these two.
  watermarkTitle: {
    id: 'app.sml.upgrade.feature.watermark.title',
    defaultMessage: 'Remove the MUSIC24 watermark.',
  },
  watermarkValueDescription: {
    id: 'app.sml.upgrade.feature.watermark.valueDescription',
    defaultMessage: 'Full size and no watermark on each new file.',
  },

  // A paid plan below its artist cap (402 `M24_LIMIT_ARTISTS` with an offer): add one paid artist
  // at the plan price minus 20 %.
  extraArtistLede: {
    id: 'app.sml.upgrade.feature.artists.extraLede',
    defaultMessage:
      'Add an artist for {price} a month, 20 % off your plan price. The new artist gets the full limits of your plan.',
  },
  extraArtistCta: {
    id: 'app.sml.upgrade.feature.artists.extraCta',
    defaultMessage: 'Add an artist for {price} a month',
  },

  genericLabel: { id: 'app.sml.upgrade.feature.generic.label', defaultMessage: 'Premium' },
  genericTitle: { id: 'app.sml.upgrade.feature.generic.title', defaultMessage: 'That one is on {plan}' },
  genericTitleWithSubject: {
    id: 'app.sml.upgrade.feature.generic.titleWithSubject',
    defaultMessage: '{subject} is on {plan}',
  },
  genericLede: {
    id: 'app.sml.upgrade.feature.generic.lede',
    defaultMessage: 'The data is already collected. {plan} shows it, from {price} a month.',
  },
  genericLedeWithoutPrice: {
    id: 'app.sml.upgrade.feature.generic.ledeWithoutPrice',
    defaultMessage: 'The data is already collected. {plan} shows it.',
  },
  genericValueTitle: { id: 'app.sml.upgrade.feature.generic.valueTitle', defaultMessage: 'Everything below' },
  genericValueDescription: {
    id: 'app.sml.upgrade.feature.generic.valueDescription',
    defaultMessage: 'The full MUSIC24 dataset, not a sample of it.',
  },
});

/**
 * `SMLUpgradeFeature` → the `MUSIC24_FEATURE_ROWS` key that carries the same number. Shared by
 * `useUpgradeCopy` (the `currentText`/`targetText` a lede quotes) and `useUpgradePlanOptions` (the
 * fact under each offered plan). Not every feature has a row: `browseDepth` and `generic`
 * read from `Music24Capabilities` fields the compare table does not list.
 */
export const FEATURE_ROW_KEY: Partial<Record<SMLUpgradeFeature, string>> = {
  [SMLUpgradeFeature.history]: 'history',
  [SMLUpgradeFeature.geography]: 'geography',
  [SMLUpgradeFeature.musicServiceFilter]: 'serviceFilter',
  [SMLUpgradeFeature.insights]: 'listeningInsights',
  [SMLUpgradeFeature.csvExport]: 'csvExport',
  [SMLUpgradeFeature.followLimit]: 'follows',
  [SMLUpgradeFeature.apiAccess]: 'api',
  [SMLUpgradeFeature.pages]: 'pages',
  [SMLUpgradeFeature.artists]: 'artists',
  [SMLUpgradeFeature.seats]: 'seats',
  [SMLUpgradeFeature.socialAccounts]: 'socialAccounts',
  [SMLUpgradeFeature.scheduledPosts]: 'scheduledPosts',
  [SMLUpgradeFeature.videoCredits]: 'videoCredits',
  [SMLUpgradeFeature.aiInsights]: 'aiInsights',
  [SMLUpgradeFeature.customDomain]: 'customDomain',
  [SMLUpgradeFeature.fanExport]: 'fanExport',
  [SMLUpgradeFeature.presave]: 'presaveServices',
  [SMLUpgradeFeature.realtimeAlerts]: 'alerts',
  [SMLUpgradeFeature.artistPortalHistory]: 'portalData',
  [SMLUpgradeFeature.earnings]: 'earnings',
  [SMLUpgradeFeature.streamingSplit]: 'streamingSplit',
  [SMLUpgradeFeature.releases]: 'releases',
  [SMLUpgradeFeature.oldSongs]: 'oldSongs',
  [SMLUpgradeFeature.fullVideo]: 'fullVideo',
  [SMLUpgradeFeature.motionArtwork]: 'fullVideo',
  [SMLUpgradeFeature.aiFootage]: 'aiClips',
  [SMLUpgradeFeature.kitLook]: 'kitLook',
  [SMLUpgradeFeature.kitZip]: 'kitZip',
  [SMLUpgradeFeature.renderLane]: 'renderLane',
  [SMLUpgradeFeature.watermark]: 'quality',
  // The pitches a day row: the technical cap of the sends has no row.
  [SMLUpgradeFeature.outreach]: 'outreachPitchesPerDay',
  [SMLUpgradeFeature.outreachSends]: 'outreachPitchesPerDay',
  [SMLUpgradeFeature.outreachRate]: 'outreachPitchesPerDay',
  [SMLUpgradeFeature.outreachMailboxes]: 'outreachMailboxes',
  [SMLUpgradeFeature.outreachAutopilot]: 'outreachAutopilot',
};

/** The row's own formatted text (e.g. "Unlimited", "60 a month") for a plan's capabilities. */
export function featureRowValue(
  feature: SMLUpgradeFeature,
  capabilities: Music24PlanCapabilities,
  intl: IntlShape
): string {
  const key = FEATURE_ROW_KEY[feature];
  if (key === undefined) {
    return '';
  }
  return MUSIC24_FEATURE_ROWS.find((row) => row.key === key)?.value(capabilities, intl) ?? '';
}

/** The row's own label (e.g. "Markets and geo filters"), or `undefined` for a feature with no row. */
export function featureRowLabel(feature: SMLUpgradeFeature): MessageDescriptor | undefined {
  const key = FEATURE_ROW_KEY[feature];
  if (key === undefined) {
    return undefined;
  }
  return MUSIC24_FEATURE_ROWS.find((row) => row.key === key)?.label;
}

/**
 * The "{month} {day}" reset date text — shared by `SMLUpgradeOffer`'s "Wait for {date}" action and
 * `SMLUpgradeQuotaMeter`'s "Resets {date}" line, so the two never drift apart.
 */
export function formatQuotaResetDate(intl: IntlShape, resetsAt: string): string {
  return intl.formatDate(new Date(resetsAt), { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * `resetsAt` is always the 1st of the *next* month at UTC midnight — quotas reset on the 1st. The
 * month a quota counts against (what design b2 labels "Auto-posts in September") is the one
 * before that, so this reads one month back from `resetsAt` before formatting.
 */
export function formatQuotaReportingMonth(intl: IntlShape, resetsAt: string): string {
  const reset = new Date(resetsAt);
  const reportingMonth = new Date(Date.UTC(reset.getUTCFullYear(), reset.getUTCMonth() - 1, 1));
  return intl.formatDate(reportingMonth, { month: 'long', timeZone: 'UTC' });
}

/** The row's own label — reused as `label`/`valueTitle` so the pill and the value list say the same word as the compare table. */
function rowLabel(key: string): MessageDescriptor {
  const row = MUSIC24_FEATURE_ROWS.find((candidate) => candidate.key === key);
  if (row === undefined) {
    throw new Error(`upgradeMessages: no MUSIC24_FEATURE_ROWS entry for "${key}".`);
  }
  return row.label;
}

/**
 * A result-first prompt (pricing spec 2026-10-08): the title names the result ("Make the lyric
 * video for Forests"), the lede names the plan and the price ("On Pro, $39 a month.").
 */
function resultCopy(
  rowKey: string,
  copy: Pick<FeatureCopy, 'title' | 'titleWithSubject' | 'valueDescription'>
): FeatureCopy {
  return {
    label: rowLabel(rowKey),
    lede: featureMessages.resultLede,
    ledeWithoutPrice: featureMessages.resultLedeWithoutPrice,
    valueTitle: rowLabel(rowKey),
    ...copy,
  };
}

export const featureCopy: Record<SMLUpgradeFeature, FeatureCopy> = {
  [SMLUpgradeFeature.history]: {
    label: featureMessages.historyLabel,
    title: featureMessages.historyTitle,
    titleWithSubject: featureMessages.historyTitleWithSubject,
    lede: featureMessages.historyLede,
    valueTitle: featureMessages.historyValueTitle,
    valueDescription: featureMessages.historyValueDescription,
  },
  [SMLUpgradeFeature.browseDepth]: {
    label: featureMessages.browseDepthLabel,
    title: featureMessages.browseDepthTitle,
    titleWithSubject: featureMessages.browseDepthTitleWithSubject,
    lede: featureMessages.browseDepthLede,
    valueTitle: featureMessages.browseDepthValueTitle,
    valueDescription: featureMessages.browseDepthValueDescription,
  },
  [SMLUpgradeFeature.geography]: {
    label: featureMessages.geographyLabel,
    title: featureMessages.geographyTitle,
    titleWithSubject: featureMessages.geographyTitleWithSubject,
    lede: featureMessages.geographyLede,
    valueTitle: featureMessages.geographyValueTitle,
    valueDescription: featureMessages.geographyValueDescription,
  },
  [SMLUpgradeFeature.musicServiceFilter]: {
    label: featureMessages.serviceLabel,
    title: featureMessages.serviceTitle,
    titleWithSubject: featureMessages.serviceTitleWithSubject,
    lede: featureMessages.serviceLede,
    valueTitle: featureMessages.serviceValueTitle,
    valueDescription: featureMessages.serviceValueDescription,
  },
  [SMLUpgradeFeature.insights]: {
    label: featureMessages.insightsLabel,
    title: featureMessages.insightsTitle,
    titleWithSubject: featureMessages.insightsTitleWithSubject,
    lede: featureMessages.insightsLede,
    valueTitle: featureMessages.insightsValueTitle,
    valueDescription: featureMessages.insightsValueDescription,
  },
  [SMLUpgradeFeature.csvExport]: {
    label: featureMessages.exportLabel,
    title: featureMessages.exportTitle,
    titleWithSubject: featureMessages.exportTitleWithSubject,
    lede: featureMessages.exportLede,
    valueTitle: featureMessages.exportValueTitle,
    valueDescription: featureMessages.exportValueDescription,
  },
  [SMLUpgradeFeature.followLimit]: {
    label: featureMessages.followLabel,
    title: featureMessages.followTitle,
    titleWithSubject: featureMessages.followTitleWithSubject,
    lede: featureMessages.followLede,
    valueTitle: featureMessages.followValueTitle,
    valueDescription: featureMessages.followValueDescription,
  },
  [SMLUpgradeFeature.apiAccess]: {
    label: featureMessages.apiAccessLabel,
    title: featureMessages.apiAccessTitle,
    titleWithSubject: featureMessages.apiAccessTitleWithSubject,
    lede: featureMessages.apiAccessLede,
    valueTitle: featureMessages.apiAccessValueTitle,
    valueDescription: featureMessages.apiAccessValueDescription,
  },
  [SMLUpgradeFeature.pages]: {
    label: rowLabel('pages'),
    title: featureMessages.pagesTitle,
    titleWithSubject: featureMessages.pagesTitle,
    lede: featureMessages.pagesBody,
    valueTitle: rowLabel('pages'),
    valueDescription: featureMessages.pagesValueDescription,
  },
  [SMLUpgradeFeature.artists]: {
    label: rowLabel('artists'),
    title: featureMessages.artistsTitle,
    titleWithSubject: featureMessages.artistsTitle,
    lede: featureMessages.artistsBody,
    valueTitle: rowLabel('artists'),
    valueDescription: featureMessages.artistsValueDescription,
  },
  [SMLUpgradeFeature.seats]: {
    label: rowLabel('seats'),
    title: featureMessages.seatsTitle,
    titleWithSubject: featureMessages.seatsTitle,
    lede: featureMessages.seatsBody,
    valueTitle: rowLabel('seats'),
    valueDescription: featureMessages.seatsValueDescription,
  },
  [SMLUpgradeFeature.socialAccounts]: {
    label: rowLabel('socialAccounts'),
    title: featureMessages.socialAccountsTitle,
    titleWithSubject: featureMessages.socialAccountsTitle,
    lede: featureMessages.socialAccountsBody,
    valueTitle: rowLabel('socialAccounts'),
    valueDescription: featureMessages.socialAccountsValueDescription,
  },
  [SMLUpgradeFeature.scheduledPosts]: {
    label: rowLabel('scheduledPosts'),
    title: featureMessages.scheduledPostsTitle,
    titleWithSubject: featureMessages.scheduledPostsTitle,
    lede: featureMessages.scheduledPostsBody,
    valueTitle: rowLabel('scheduledPosts'),
    valueDescription: featureMessages.scheduledPostsValueDescription,
  },
  [SMLUpgradeFeature.videoCredits]: {
    label: rowLabel('videoCredits'),
    title: featureMessages.videoCreditsTitle,
    titleWithSubject: featureMessages.videoCreditsTitle,
    lede: featureMessages.videoCreditsBody,
    valueTitle: rowLabel('videoCredits'),
    valueDescription: featureMessages.videoCreditsValueDescription,
  },
  [SMLUpgradeFeature.aiInsights]: {
    label: rowLabel('aiInsights'),
    title: featureMessages.aiInsightsTitle,
    titleWithSubject: featureMessages.aiInsightsTitle,
    lede: featureMessages.aiInsightsBody,
    valueTitle: rowLabel('aiInsights'),
    valueDescription: featureMessages.aiInsightsValueDescription,
  },
  [SMLUpgradeFeature.customDomain]: {
    label: rowLabel('customDomain'),
    title: featureMessages.customDomainTitle,
    titleWithSubject: featureMessages.customDomainTitle,
    lede: featureMessages.customDomainBody,
    valueTitle: rowLabel('customDomain'),
    valueDescription: featureMessages.customDomainValueDescription,
  },
  [SMLUpgradeFeature.fanExport]: {
    label: rowLabel('fanExport'),
    title: featureMessages.fanExportTitle,
    titleWithSubject: featureMessages.fanExportTitle,
    lede: featureMessages.fanExportBody,
    valueTitle: rowLabel('fanExport'),
    valueDescription: featureMessages.fanExportValueDescription,
  },
  [SMLUpgradeFeature.presave]: {
    label: rowLabel('presaveServices'),
    title: featureMessages.presaveTitle,
    titleWithSubject: featureMessages.presaveTitleWithSubject,
    lede: featureMessages.presaveBody,
    valueTitle: rowLabel('presaveServices'),
    valueDescription: featureMessages.presaveValueDescription,
  },
  [SMLUpgradeFeature.realtimeAlerts]: {
    label: rowLabel('alerts'),
    title: featureMessages.realtimeAlertsTitle,
    titleWithSubject: featureMessages.realtimeAlertsTitle,
    lede: featureMessages.realtimeAlertsBody,
    valueTitle: rowLabel('alerts'),
    valueDescription: featureMessages.realtimeAlertsValueDescription,
  },
  [SMLUpgradeFeature.artistPortalHistory]: {
    label: rowLabel('portalData'),
    title: featureMessages.artistPortalHistoryTitle,
    titleWithSubject: featureMessages.artistPortalHistoryTitle,
    lede: featureMessages.artistPortalHistoryBody,
    valueTitle: rowLabel('portalData'),
    valueDescription: featureMessages.artistPortalHistoryValueDescription,
  },
  [SMLUpgradeFeature.earnings]: {
    label: featureMessages.earningsLabel,
    title: featureMessages.earningsTitle,
    titleWithSubject: featureMessages.earningsTitle,
    lede: featureMessages.resultLede,
    ledeWithoutPrice: featureMessages.resultLedeWithoutPrice,
    valueTitle: featureMessages.earningsLabel,
    valueDescription: featureMessages.earningsValueDescription,
  },
  [SMLUpgradeFeature.aiPriority]: {
    label: featureMessages.aiPriorityLabel,
    title: featureMessages.aiPriorityTitle,
    titleWithSubject: featureMessages.aiPriorityTitle,
    lede: featureMessages.aiPriorityLede,
    ledeWithoutPrice: featureMessages.aiPriorityLedeWithoutPrice,
    valueTitle: featureMessages.aiPriorityLabel,
    valueDescription: featureMessages.aiPriorityValueDescription,
  },
  [SMLUpgradeFeature.streamingSplit]: {
    label: featureMessages.streamingSplitLabel,
    title: featureMessages.streamingSplitTitle,
    titleWithSubject: featureMessages.streamingSplitTitle,
    lede: featureMessages.resultLede,
    ledeWithoutPrice: featureMessages.resultLedeWithoutPrice,
    valueTitle: featureMessages.streamingSplitLabel,
    valueDescription: featureMessages.streamingSplitValueDescription,
  },
  [SMLUpgradeFeature.releases]: resultCopy('releases', {
    title: featureMessages.releasesTitle,
    titleWithSubject: featureMessages.releasesTitleWithSubject,
    valueDescription: featureMessages.releasesValueDescription,
  }),
  [SMLUpgradeFeature.oldSongs]: resultCopy('oldSongs', {
    title: featureMessages.oldSongsTitle,
    titleWithSubject: featureMessages.oldSongsTitleWithSubject,
    valueDescription: featureMessages.oldSongsValueDescription,
  }),
  [SMLUpgradeFeature.fullVideo]: resultCopy('fullVideo', {
    title: featureMessages.fullVideoTitle,
    titleWithSubject: featureMessages.fullVideoTitleWithSubject,
    valueDescription: featureMessages.fullVideoValueDescription,
  }),
  [SMLUpgradeFeature.motionArtwork]: resultCopy('fullVideo', {
    title: featureMessages.motionArtworkTitle,
    titleWithSubject: featureMessages.motionArtworkTitleWithSubject,
    valueDescription: featureMessages.motionArtworkValueDescription,
  }),
  [SMLUpgradeFeature.aiFootage]: resultCopy('aiClips', {
    title: featureMessages.aiFootageTitle,
    titleWithSubject: featureMessages.aiFootageTitleWithSubject,
    valueDescription: featureMessages.aiFootageValueDescription,
  }),
  [SMLUpgradeFeature.kitLook]: resultCopy('kitLook', {
    title: featureMessages.kitLookTitle,
    titleWithSubject: featureMessages.kitLookTitleWithSubject,
    valueDescription: featureMessages.kitLookValueDescription,
  }),
  [SMLUpgradeFeature.kitZip]: resultCopy('kitZip', {
    title: featureMessages.kitZipTitle,
    titleWithSubject: featureMessages.kitZipTitleWithSubject,
    valueDescription: featureMessages.kitZipValueDescription,
  }),
  [SMLUpgradeFeature.renderLane]: resultCopy('renderLane', {
    title: featureMessages.renderLaneTitle,
    titleWithSubject: featureMessages.renderLaneTitle,
    valueDescription: featureMessages.renderLaneValueDescription,
  }),
  [SMLUpgradeFeature.watermark]: resultCopy('quality', {
    title: featureMessages.watermarkTitle,
    titleWithSubject: featureMessages.watermarkTitle,
    valueDescription: featureMessages.watermarkValueDescription,
  }),
  [SMLUpgradeFeature.outreach]: resultCopy('outreachPitchesPerDay', {
    title: featureMessages.outreachTitle,
    titleWithSubject: featureMessages.outreachTitleWithSubject,
    valueDescription: featureMessages.outreachValueDescription,
  }),
  [SMLUpgradeFeature.outreachSends]: resultCopy('outreachPitchesPerDay', {
    title: featureMessages.outreachSendsTitle,
    titleWithSubject: featureMessages.outreachSendsTitle,
    valueDescription: featureMessages.outreachSendsValueDescription,
  }),
  [SMLUpgradeFeature.outreachMailboxes]: resultCopy('outreachMailboxes', {
    title: featureMessages.outreachMailboxesTitle,
    titleWithSubject: featureMessages.outreachMailboxesTitle,
    valueDescription: featureMessages.outreachMailboxesValueDescription,
  }),
  [SMLUpgradeFeature.outreachAutopilot]: resultCopy('outreachAutopilot', {
    title: featureMessages.outreachAutopilotTitle,
    titleWithSubject: featureMessages.outreachAutopilotTitle,
    valueDescription: featureMessages.outreachAutopilotValueDescription,
  }),
  [SMLUpgradeFeature.outreachRate]: resultCopy('outreachPitchesPerDay', {
    title: featureMessages.outreachRateTitle,
    titleWithSubject: featureMessages.outreachRateTitle,
    valueDescription: featureMessages.outreachRateValueDescription,
  }),
  [SMLUpgradeFeature.outreachExport]: resultCopy('csvExport', {
    title: featureMessages.outreachExportTitle,
    titleWithSubject: featureMessages.outreachExportTitle,
    valueDescription: featureMessages.outreachExportValueDescription,
  }),
  // X has no compare row of its own: the pill says the social accounts row, the value list the title.
  [SMLUpgradeFeature.x]: {
    label: rowLabel('socialAccounts'),
    title: featureMessages.xTitle,
    titleWithSubject: featureMessages.xTitle,
    lede: featureMessages.xBody,
    valueTitle: featureMessages.xTitle,
    valueDescription: featureMessages.xBody,
  },
  [SMLUpgradeFeature.generic]: {
    label: featureMessages.genericLabel,
    title: featureMessages.genericTitle,
    titleWithSubject: featureMessages.genericTitleWithSubject,
    lede: featureMessages.genericLede,
    ledeWithoutPrice: featureMessages.genericLedeWithoutPrice,
    valueTitle: featureMessages.genericValueTitle,
    valueDescription: featureMessages.genericValueDescription,
  },
};
